import { Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class NavigationInterceptorService implements OnDestroy {
  private isNavigating = false;
  private transitionDuration = 200; // milliseconds
  private destroy$ = new Subject<void>();
  private navigationTimeouts: number[] = [];

  constructor(private router: Router) {
    this.initNavigationListeners();
    this.initBrowserEventListeners();
  }

  /**
   * Initialize navigation event listeners
   */
  private initNavigationListeners(): void {
    // Subscribe to navigation start events
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart && !this.isNavigating),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.startNavigation();
      });

    // Subscribe to navigation end events (success, cancel, error)
    this.router.events
      .pipe(
        filter(
          (event) =>
            (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) &&
            this.isNavigating
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        if (event instanceof NavigationError) {
          console.error('Navigation error:', event.error);
        }
        this.endNavigation();
      });
  }

  /**
   * Initialize browser event listeners for back/forward navigation
   */
  private initBrowserEventListeners(): void {
    // Handle browser back/forward navigation
    fromEvent(window, 'popstate')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // If we're not already navigating, start a navigation transition
        if (!this.isNavigating) {
          this.startNavigation();

          // End the navigation after a short delay
          const timeoutId = window.setTimeout(() => {
            this.endNavigation();
          }, this.transitionDuration);

          this.navigationTimeouts.push(timeoutId);
        }
      });
  }

  /**
   * Start navigation transition
   */
  private startNavigation(): void {
    this.isNavigating = true;

    // Add the navigating class to the body to fade out the current page
    // Using requestAnimationFrame ensures this happens in the next paint cycle
    requestAnimationFrame(() => {
      document.body.classList.add('page-navigating');
    });
  }

  /**
   * End navigation transition
   */
  private endNavigation(): void {
    // Remove the class after a short delay to ensure the new page has loaded
    const timeoutId = window.setTimeout(() => {
      // Using requestAnimationFrame ensures this happens in the next paint cycle
      requestAnimationFrame(() => {
        document.body.classList.remove('page-navigating');
        this.isNavigating = false;
      });
    }, 50); // Short delay to ensure the new page has loaded

    this.navigationTimeouts.push(timeoutId);
  }

  /**
   * Clear all navigation timeouts
   */
  private clearTimeouts(): void {
    this.navigationTimeouts.forEach((id) => window.clearTimeout(id));
    this.navigationTimeouts = [];
  }

  /**
   * Clean up subscriptions when service is destroyed
   */
  ngOnDestroy(): void {
    this.clearTimeouts();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
